import { useParams } from "react-router-dom";
import { Avatar } from "@frontend/lyng/avatar/Avatar";
import { useVisitByIdQuery } from "../../api/generated/graphql";
import { DateTime } from "luxon";
import { match, P } from "ts-pattern";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { useCareContext } from "../../providers";
import { useTranslate } from "@tolgee/react";
import { useMemo } from "react";
import { Paragraph } from "@frontend/lyng/typography";

// CaregiverCard component
export type CaregiverStatus =
  | "clockedIn"
  | "runningLate"
  | "clockedOut"
  | "missed";

interface CaregiverCardProps {
  name: string;
  status: CaregiverStatus | null;
}

const getStatusTextAndClass = (
  status: CaregiverStatus | null,
  t: (k: string) => string,
) => {
  return match(status)
    .with("clockedIn", () => [t("status.clockedIn"), "text-greyscale-400"])
    .with("runningLate", () => [t("status.runningLate"), "text-accent-400"])
    .with("clockedOut", () => [t("status.clockedOut"), "text-greyscale-400"])
    .with("missed", () => [t("status.missed"), "text-critical-400"])
    .with(P.nullish, () => [t(""), ""])
    .exhaustive();
};

export const CaregiverCard = ({ name, status }: CaregiverCardProps) => {
  const { t } = useTranslate();
  const [firstName, ...rest] = name.split(" ");
  const lastName = rest.join(" ");

  const [statusText, statusClass] = useMemo(
    () => getStatusTextAndClass(status, t),
    [status, t],
  );

  return (
    <div className="flex flex-row items-center justify-between py-3 px-2 border-b border-greyscale-800">
      <div className="flex flex-row items-center">
        <Avatar
          firstName={firstName}
          lastName={lastName}
          size="md"
          randomizeColor={true}
          className="mr-4"
        />
        <Paragraph size="m">{name}</Paragraph>
      </div>
      {statusText && (
        <Paragraph size="m" className={statusClass}>
          {statusText}
        </Paragraph>
      )}
    </div>
  );
};

const getStatus = (
  startTime: string | undefined,
  clockInTime: string | null,
  clockOutTime: string | null,
) => {
  const now = DateTime.now();

  if (
    !clockInTime &&
    startTime &&
    now.diff(DateTime.fromISO(startTime), "minutes").minutes < 1
  ) {
    return null;
  }

  if (
    !clockInTime &&
    startTime &&
    now.diff(DateTime.fromISO(startTime), "minutes").minutes > 15
  ) {
    return "missed";
  }

  const startTimeDate = startTime ? DateTime.fromISO(startTime) : null;
  if (
    !clockOutTime &&
    startTimeDate &&
    now.diff(startTimeDate, "minutes").minutes > 1
  ) {
    return "runningLate";
  }

  if (clockOutTime) {
    return "clockedOut";
  }

  return "clockedIn";
};

// Main CaregiversList page
export const CaregiversList = () => {
  const { id } = useParams();
  const { data } = useVisitByIdQuery({
    variables: {
      visitInstanceId: id ?? "",
    },
    skip: !id,
  });
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  if (!data?.visitById || !data.visitById.visitorsV2) {
    return <div>Loading...</div>;
  }

  const caregivers = data?.visitById?.visitorsV2;
  return (
    <div className="flex flex-col min-h-screen bg-main">
      <div className="flex flex-col gap-1 px-2">
        {caregivers.map((c) => (
          <CaregiverCard
            key={c.id}
            name={nameOrderFn(c)}
            status={getStatus(
              data.visitById?.start,
              c.clockInTime,
              c.clockOutTime,
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default CaregiversList;
